import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Tooltip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import MainCard from '../../../../components/cards/MainCard';
import GlobalDateRangeSelector from '../../../../components/GlobalDateRangeSelector';
import GlobalAnimatedProgressBar from '../../../../components/GlobalAnimatedProgressBar';
import BusinessIcon from '@mui/icons-material/Workspaces';
import HelpIcon from "@mui/icons-material/HelpOutline";
import CountUp from 'react-countup';
import GlobalPagination from '../../../../components/GlobalPagination';
import useDepartmentAnalytics from '../../use-data-hooks/useDepartmentAnalytics';
import useLineManagerAnalytics from '../../use-data-hooks/useLineManagerAnalytics';
import { getDateRangeDisplayText, calculateDateRange, toLocalDateString } from '../../../../utils/dateRangeUtils';
import { useDynamicBreakpoints } from '../../../../utils/breakpointUtils';

const UserViewDepartmentOverview = ({ tenancyId, user }) => {
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);

    const isVerySmallScreen = matchDownSM && window.innerWidth <= 375;

    const [dateRange, setDateRange] = useState('6months');
    const [activeTab, setActiveTab] = useState(0); // 0 = Simulations, 1 = Courses
    const [viewTab, setViewTab] = useState(0); // 0 = Department, 1 = My Team (if user is a line manager)

    // Custom date range states
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Pagination states
    const [departmentPage, setDepartmentPage] = useState(1);
    const [teamPage, setTeamPage] = useState(1);
    const itemsPerPage = matchDownSM ? 3 : 5; // Back to original items per page

    const [selectedMemberType, setSelectedMemberType] = useState('all');

    // Calculate actual dates based on selection using the utility
    const dateParams = useMemo(() => {
        if (dateRange === 'custom' && customStartDate && customEndDate) {
            return { startDate: customStartDate, endDate: customEndDate };
        }
        const { startDate, endDate } = calculateDateRange(dateRange, customStartDate, customEndDate);
        return {
            startDate: toLocalDateString(startDate),
            endDate: toLocalDateString(endDate)
        };
    }, [dateRange, customStartDate, customEndDate]);

    // Use real API hook for department data
    const { data, isLoading, isError, error } = useDepartmentAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate
    });

    // Hook for line manager data (to see if user manages anyone)
    const {
        data: lineManagerData,
        isLoading: lineManagerLoading,
        isError: lineManagerError
    } = useLineManagerAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        lineManagerId: user?.id // Filter by current user's ID to see if they manage anyone
    });

    const {
        data: allLineManagersData,
        isLoading: allLineManagersLoading,
        isError: allLineManagersError,
    } = useLineManagerAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
    });

    const lineManagerLookup = useMemo(() => {
        if (!allLineManagersData?.lineManagerData) {
            return new Map();
        }

        const lookup = new Map();
        allLineManagersData.lineManagerData.forEach(manager => {
            lookup.set(manager.lineManagerId, {
                isLineManager: true,
                userCount: manager.userCount,
                lineManagerName: manager.lineManagerName,
                lineManagerEmail: manager.lineManagerEmail
            });
        });

        return lookup;
    }, [allLineManagersData?.lineManagerData]);

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    const handleCustomStartDateChange = (event) => {
        setCustomStartDate(event.target.value);
    };

    const handleCustomEndDateChange = (event) => {
        setCustomEndDate(event.target.value);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setDepartmentPage(1);
        setTeamPage(1);
    };

    const handleViewTabChange = (event, newValue) => {
        setViewTab(newValue);
        setDepartmentPage(1);
        setTeamPage(1);
        setSelectedMemberType('all');
    };

    const handleDepartmentPageChange = (event, value) => {
        setDepartmentPage(value);
    };

    const handleTeamPageChange = (event, value) => {
        setTeamPage(value);
    };

    const handleMemberTypeFilterChange = (event) => {
        setSelectedMemberType(event.target.value);
        setTeamPage(1);
    };

    // Get user's department for filtering
    const userDepartment = user?.department || null;

    // Check if user has direct reports
    const hasDirectReports = lineManagerData?.directReportsData && lineManagerData.directReportsData.length > 0;

    // Process department data for the selected tab - filtered to user's department only
    const processedDepartmentData = useMemo(() => {
        if (!data?.departmentData) return [];

        return data.departmentData
            .filter(dept => dept.departmentName && dept.departmentName.trim() !== '')
            .filter(dept => {
                // Only show departments the user belongs to
                if (!userDepartment) return false;
                return dept.departmentName === userDepartment;
            })
            .map(dept => {
                if (activeTab === 0) {
                    const clickRate = dept.simulationsSent > 0
                        ? (dept.simulationsClicked / dept.simulationsSent) * 100
                        : 0;

                    return {
                        ...dept,
                        rate: Math.round(clickRate * 100) / 100,
                        displayMetric: dept.simulationsClicked,
                        totalSent: dept.simulationsSent
                    };
                } else {
                    const completionRate = dept.coursesSent > 0
                        ? (dept.coursesCompleted / dept.coursesSent) * 100
                        : 0;

                    return {
                        ...dept,
                        rate: Math.round(completionRate * 100) / 100,
                        displayMetric: dept.coursesCompleted,
                        totalSent: dept.coursesSent
                    };
                }
            })
            .sort((a, b) => {
                if (activeTab === 0) {
                    return b.simulationsClicked - a.simulationsClicked;
                } else {
                    return b.coursesCompleted - a.coursesCompleted;
                }
            });
    }, [data?.departmentData, activeTab, userDepartment]);

    const processedDirectReportsData = useMemo(() => {
        if (!hasDirectReports) return [];

        const directReports = lineManagerData.directReportsData;

        const processedData = directReports.map(report => {
            const isLineManager = lineManagerLookup.has(report.userId);
            const lineManagerInfo = lineManagerLookup.get(report.userId);

            if (activeTab === 0) { // Simulations
                const clickRate = report.simulationsSent > 0
                    ? (report.simulationsClicked / report.simulationsSent) * 100
                    : 0;

                return {
                    ...report,
                    displayName: report.userName || report.email,
                    rate: Math.round(clickRate * 100) / 100,
                    displayMetric: report.simulationsClicked,
                    totalSent: report.simulationsSent,
                    isLineManager,
                    userCount: lineManagerInfo?.userCount || 0
                };
            } else { // Courses
                const completionRate = report.coursesSent > 0
                    ? (report.coursesCompleted / report.coursesSent) * 100
                    : 0;

                return {
                    ...report,
                    displayName: report.userName || report.email,
                    rate: Math.round(completionRate * 100) / 100,
                    displayMetric: report.coursesCompleted,
                    totalSent: report.coursesSent,
                    isLineManager,
                    userCount: lineManagerInfo?.userCount || 0
                };
            }
        }).filter(report => {
            if (selectedMemberType === 'all') return true;
            if (selectedMemberType === 'line-managers') return report.isLineManager;
            return true;
        }).sort((a, b) => {
            if (activeTab === 0) {
                return b.simulationsClicked - a.simulationsClicked;
            } else {
                return b.coursesCompleted - a.coursesCompleted;
            }
        });

        return processedData;
    }, [lineManagerData?.directReportsData, activeTab, selectedMemberType, hasDirectReports, lineManagerLookup]);

    // Get visible data for current page
    const visibleDepartments = useMemo(() => {
        const start = (departmentPage - 1) * itemsPerPage;
        return processedDepartmentData.slice(start, start + itemsPerPage);
    }, [processedDepartmentData, departmentPage, itemsPerPage]);

    const visibleDirectReports = useMemo(() => {
        const start = (teamPage - 1) * itemsPerPage;
        return processedDirectReportsData.slice(start, start + itemsPerPage);
    }, [processedDirectReportsData, teamPage, itemsPerPage]);

    // Calculate overall statistics based on current view
    const overallStats = useMemo(() => {
        if (viewTab === 0) {
            // Department view
            if (!processedDepartmentData.length) return {
                totalDepartments: 0,
                totalUsers: 0,
                totalSent: 0,
                totalInteracted: 0
            };

            const departmentStats = processedDepartmentData.reduce((acc, dept) => {
                acc.totalUsers += dept.userCount || 0;
                if (activeTab === 0) {
                    acc.totalSent += dept.simulationsSent || 0;
                    acc.totalInteracted += dept.simulationsClicked || 0;
                } else {
                    acc.totalSent += dept.coursesSent || 0;
                    acc.totalInteracted += dept.coursesCompleted || 0;
                }
                return acc;
            }, {
                totalUsers: 0,
                totalSent: 0,
                totalInteracted: 0
            });

            return {
                totalDepartments: processedDepartmentData.length,
                totalUsers: departmentStats.totalUsers,
                totalSent: departmentStats.totalSent,
                totalInteracted: departmentStats.totalInteracted
            };
        } else {
            // Team view
            if (!processedDirectReportsData.length) return {
                totalTeamMembers: 0,
                totalLineManagers: 0,
                totalSent: 0,
                totalInteracted: 0
            };

            const teamStats = processedDirectReportsData.reduce((acc, report) => {
                if (report.isLineManager) acc.totalLineManagers += 1;
                if (activeTab === 0) {
                    acc.totalSent += report.simulationsSent || 0;
                    acc.totalInteracted += report.simulationsClicked || 0;
                } else {
                    acc.totalSent += report.coursesSent || 0;
                    acc.totalInteracted += report.coursesCompleted || 0;
                }
                return acc;
            }, {
                totalSent: 0,
                totalInteracted: 0,
                totalLineManagers: 0
            });

            return {
                totalTeamMembers: processedDirectReportsData.length,
                totalLineManagers: teamStats.totalLineManagers,
                totalSent: teamStats.totalSent,
                totalInteracted: teamStats.totalInteracted
            };
        }
    }, [processedDepartmentData, processedDirectReportsData, activeTab, viewTab]);

    React.useEffect(() => {
        if (selectedMemberType === 'line-managers' && overallStats.totalLineManagers === 0) {
            setSelectedMemberType('all');
        }
    }, [overallStats.totalLineManagers, selectedMemberType]);

    if (isLoading || (hasDirectReports && (lineManagerLoading || allLineManagersLoading))) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>
                        Loading analytics data...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (isError || (hasDirectReports && (lineManagerError || allLineManagersError))) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <Typography variant="h6" color="error">
                        Error loading analytics data
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    const currentData = viewTab === 0 ? processedDepartmentData : processedDirectReportsData;
    const visibleData = viewTab === 0 ? visibleDepartments : visibleDirectReports;
    const currentPage = viewTab === 0 ? departmentPage : teamPage;
    const handlePageChange = viewTab === 0 ? handleDepartmentPageChange : handleTeamPageChange;

    return (
        <MainCard
            content={false}
            sx={{
                width: "100%",
                bgcolor: "background.default",
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
            border={false}
            boxShadow={true}
        >
            <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2), flexGrow: 1 }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: matchDownSM ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: matchDownSM ? 'flex-start' : 'center',
                    mb: 2,
                    gap: matchDownSM ? 1.5 : 2
                }}>
                    <Typography
                        variant={matchDownSM ? "subtitle1" : "h6"}
                        sx={{
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            flex: '0 0 auto'
                        }}
                    >
                        <BusinessIcon sx={{ mr: 0.5, fontSize: matchDownSM ? '1rem' : '1.2rem' }} color="primary" />
                        Overview
                    </Typography>

                    <Box sx={{
                        flex: matchDownSM ? '1 1 auto' : '0 0 auto',
                        minWidth: matchDownSM ? '100%' : '200px',
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexDirection: matchDownSM ? 'column' : 'row'
                    }}>
                        <Box sx={{ minWidth: matchDownSM ? '100%' : 300 }}>
                            <GlobalDateRangeSelector
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                onCustomStartDateChange={handleCustomStartDateChange}
                                onCustomEndDateChange={handleCustomEndDateChange}
                                customStartDate={customStartDate}
                                customEndDate={customEndDate}
                                includeCustom={true}
                                fullWidth
                                size="small"
                            />
                        </Box>

                        <Tooltip title={hasDirectReports ? "View your department and team performance" : "View your department's simulation and course performance"}>
                            <IconButton size="small">
                                <HelpIcon sx={{ color: "secondary.main" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Current date range indicator */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Current: {getDateRangeDisplayText(dateRange, customStartDate, customEndDate)}
                </Typography>

                {/* Tabs for Simulations vs Courses */}
                <Box sx={{ mb: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={matchDownSM ? "fullWidth" : "standard"}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab
                            label="Simulations"
                        />
                        <Tab
                            label="Courses"
                        />
                    </Tabs>
                </Box>

                {/* Overall Statistics */}
                <Grid container spacing={isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 2)} sx={{ mb: 3 }}>
                    {viewTab === 0 ? (
                        <>
                            <Grid item xs={6} sm={6} md={3}>
                                <MainCard sx={{
                                    textAlign: 'center',
                                    bgcolor: 'background.default',
                                    p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                                }}>
                                    <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                        <CountUp
                                            start={0}
                                            end={overallStats.totalDepartments || 0}
                                            duration={1}
                                            preserveValue
                                        />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Departments</Typography>
                                </MainCard>
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <MainCard sx={{
                                    textAlign: 'center',
                                    bgcolor: 'background.default',
                                    p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                                }}>
                                    <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                        <CountUp
                                            start={0}
                                            end={overallStats.totalUsers || 0}
                                            duration={1.5}
                                            preserveValue
                                        />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Users</Typography>
                                </MainCard>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={6} sm={6} md={3}>
                                <MainCard sx={{
                                    textAlign: 'center',
                                    bgcolor: 'background.default',
                                    p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                                }}>
                                    <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                        <CountUp
                                            start={0}
                                            end={overallStats.totalTeamMembers || 0}
                                            duration={1}
                                            preserveValue
                                        />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Team Members</Typography>
                                </MainCard>
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <MainCard sx={{
                                    textAlign: 'center',
                                    bgcolor: 'background.default',
                                    p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                                }}>
                                    <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                        <CountUp
                                            start={0}
                                            end={overallStats.totalLineManagers || 0}
                                            duration={1.1}
                                            preserveValue
                                        />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Line Managers</Typography>
                                </MainCard>
                            </Grid>
                        </>
                    )}
                    <Grid item xs={6} sm={6} md={3}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={overallStats.totalSent || 0}
                                    duration={1.2}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {activeTab === 0 ? 'Sims Sent' : 'Courses Sent'}
                            </Typography>
                        </MainCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={overallStats.totalInteracted || 0}
                                    duration={1.8}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {activeTab === 0 ? 'Sims Clicked' : 'Courses Completed'}
                            </Typography>
                        </MainCard>
                    </Grid>
                </Grid>

                {/* View Tabs - Only show if user has direct reports */}
                {hasDirectReports && (
                    <Box sx={{ mb: 2 }}>
                        <Tabs
                            value={viewTab}
                            onChange={handleViewTabChange}
                            variant={matchDownSM ? "fullWidth" : "standard"}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab
                                label="Department"
                                iconPosition="start"
                                sx={{ minHeight: 48 }}
                            />
                            <Tab
                                label="My Team"
                                iconPosition="start"
                                sx={{ minHeight: 48 }}
                            />
                        </Tabs>
                    </Box>
                )}

                {/* Main Content Section */}
                <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                    <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2) }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: matchDownSM ? 'flex-start' : 'center',
                            mb: 2,
                            flexDirection: matchDownSM ? 'column' : 'row',
                            gap: matchDownSM ? 2 : 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant={matchDownSM ? "body1" : "subtitle1"} sx={{ fontWeight: 600 }}>
                                    {activeTab === 0 ? 'Simulation' : 'Course'} Performance by {viewTab === 0 ? 'Department' : 'Team Member'}
                                </Typography>
                                <Chip
                                    label={currentData.length}
                                    size="small"
                                    color="primary"
                                />
                            </Box>

                            {currentData.length > 0 && viewTab === 1 && (
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: matchDownSM ? 'column' : 'row',
                                    width: matchDownSM ? '100%' : 'auto',
                                    alignItems: 'center'
                                }}>
                                    <FormControl size="small" sx={{ minWidth: matchDownSM ? '100%' : '200px' }}>
                                        <InputLabel id="member-type-filter-label">
                                            Filter by Member Type
                                        </InputLabel>
                                        <Select
                                            labelId="member-type-filter-label"
                                            value={selectedMemberType}
                                            onChange={handleMemberTypeFilterChange}
                                            label="Filter by Member Type"
                                        >
                                            <MenuItem value="all">All Members</MenuItem>
                                            {overallStats.totalLineManagers > 0 && (
                                                <MenuItem value="line-managers">Line Managers</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>

                        {currentData.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                No {activeTab === 0 ? 'simulation' : 'course'} data available for {viewTab === 0 ? 'your department' : 'your team'}.
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {viewTab === 1 && selectedMemberType !== 'all' ?
                                            `Showing ${currentData.length} ${selectedMemberType === 'line-managers' ? 'line managers' : 'members'}` :
                                            `Showing ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, currentData.length)} of ${currentData.length} ${viewTab === 0 ? 'departments' : 'team members'}`
                                        }
                                    </Typography>
                                </Box>

                                {visibleData.length === 0 && viewTab === 1 && selectedMemberType !== 'all' ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No {selectedMemberType === 'line-managers' ? 'line managers' : 'members'} found in the selected date range.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Back to original stacked card layout */}
                                        {visibleData.map((item) => (
                                            <MainCard key={viewTab === 0 ? item.departmentId : item.userId} sx={{ mb: 2, bgcolor: "background.default" }} content={false}>
                                                <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2) }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        mb: matchDownSM ? 2 : 1,
                                                        gap: 1
                                                    }}>
                                                        {/* Name */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 1
                                                        }}>
                                                            <Typography
                                                                variant={isVerySmallScreen ? "body2" : (matchDownSM ? "body1" : "subtitle2")}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    flex: 1,
                                                                    pr: 1,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {viewTab === 0 ? item.departmentName : item.displayName}
                                                            </Typography>

                                                            {/* Performance Indicator */}
                                                            <Box sx={{
                                                                textAlign: 'right',
                                                                flex: '0 0 auto'
                                                            }}>
                                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                    {activeTab === 0 ? 'Click Rate' : 'Completion Rate'}
                                                                </Typography>
                                                                <Typography variant={isVerySmallScreen ? "caption" : "body2"} fontWeight="bold">
                                                                    {item.rate.toFixed(1)}%
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Chips */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            gap: isVerySmallScreen ? 0.5 : 1,
                                                            flexWrap: 'wrap',
                                                            '& .MuiChip-root': {
                                                                minWidth: 0,
                                                                flex: isVerySmallScreen ? '1 1 auto' : '0 0 auto'
                                                            }
                                                        }}>
                                                            {/* Show line manager indicator for team members */}
                                                            {viewTab === 1 && item.isLineManager && (
                                                                <Chip
                                                                    label={`Manages ${item.userCount} ${item.userCount === 1 ? 'person' : 'people'}`}
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                        height: isVerySmallScreen ? 20 : 24,
                                                                        '& .MuiChip-label': {
                                                                            px: isVerySmallScreen ? 0.5 : 1
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            {/* Show department for team members */}
                                                            {viewTab === 1 && item.department && (
                                                                <Chip
                                                                    label={item.department}
                                                                    size="small"
                                                                    color="default"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                        height: isVerySmallScreen ? 20 : 24,
                                                                        '& .MuiChip-label': {
                                                                            px: isVerySmallScreen ? 0.5 : 1
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            {/* Show user count for departments */}
                                                            {viewTab === 0 && (
                                                                <Chip
                                                                    label={`${item.userCount} users`}
                                                                    size="small"
                                                                    color="default"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                        height: isVerySmallScreen ? 20 : 24,
                                                                        '& .MuiChip-label': {
                                                                            px: isVerySmallScreen ? 0.5 : 1
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            <Chip
                                                                label={`${item.totalSent} sent`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                    height: isVerySmallScreen ? 20 : 24,
                                                                    '& .MuiChip-label': {
                                                                        px: isVerySmallScreen ? 0.5 : 1
                                                                    }
                                                                }}
                                                            />
                                                            <Chip
                                                                label={`${item.displayMetric} ${activeTab === 0 ? 'clicked' : 'completed'}`}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                    height: isVerySmallScreen ? 20 : 24,
                                                                    '& .MuiChip-label': {
                                                                        px: isVerySmallScreen ? 0.5 : 1
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Performance Bar */}
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <GlobalAnimatedProgressBar
                                                                percentage={item.rate}
                                                                backgroundColor="grey.200"
                                                                colorScheme={activeTab === 0 ? "gradient" : "primary"}
                                                                showLabel={false}
                                                                showPercentage={false}
                                                                showCounts={false}
                                                                textPosition="none"
                                                                showShineEffect={true}
                                                                animationDuration={1.2}
                                                                triggerAnimation={true}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </MainCard>
                                        ))}

                                        {/* Pagination */}
                                        {visibleData.length > 0 && currentData.length > itemsPerPage && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                <GlobalPagination
                                                    count={Math.ceil(currentData.length / itemsPerPage)}
                                                    page={currentPage}
                                                    onChange={handlePageChange}
                                                    showFirstButton={false}
                                                    showLastButton={false}
                                                    size={matchDownSM ? "small" : "medium"}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </MainCard>
            </Box>
        </MainCard>
    );
};

export default UserViewDepartmentOverview;